import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';

import {useState} from 'react';

// Floating UI offsets are CSS pixels; this is two grid spacing steps.
const PANEL_GAP = 8;

type BulkOperationsPanelPositionOptions = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

/** Position against the grid footer while keeping the trigger as the interaction reference. */
export function useBulkOperationsPanelPosition(options: BulkOperationsPanelPositionOptions) {
  const floating = useFloating({
    open: options.isOpen,
    onOpenChange: (isOpen) => (isOpen ? options.onOpen() : options.onClose()),
    placement: 'top-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(PANEL_GAP),
      flip(),
      shift({padding: PANEL_GAP}),
      size({
        padding: PANEL_GAP,
        apply({availableHeight, elements}) {
          elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
        },
      }),
    ],
  });
  const click = useClick(floating.context);
  const dismiss = useDismiss(floating.context);
  const role = useRole(floating.context, {role: 'dialog'});
  const interactions = useInteractions([click, dismiss, role]);

  const {setReference: setTriggerReference, setPositionReference} = floating.refs;
  // Floating UI's setters are stable. Keep the callback ref stable too: replacing it on each
  // render detaches/reattaches the position reference and recursively schedules another render.
  const [setReference] = useState(() => (button: HTMLButtonElement | null) => {
    setTriggerReference(button);
    setPositionReference(button?.closest('[data-eag-grid-footer]') ?? button);
  });

  return {...floating, ...interactions, setReference};
}
